// Import React and useState hook for managing component state
import React, { useState } from 'react';
// Import routing components for navigation and getting current page info
import { Link, useLocation, useNavigate } from 'react-router-dom';
// Import various icons from Lucide React icon library
import { 
  Home,        // House icon
  CheckSquare, // Checkbox icon for tasks
  Calendar,    // Calendar icon for dates
  Folder,      // Folder icon for matters
  FolderOpen,  // Open folder icon for projects
  Menu,        // Hamburger menu icon
  X,           // Close/X icon
  LogOut,      // Sign out icon
  Download,    // Download icon
  User,        // User profile icon
  Settings,    // Settings gear icon
  FileText,    // Document icon for templates
  BarChart2,   // Chart icon for overview
  Inbox        // Inbox icon
} from 'lucide-react';
// Import authentication functions and hooks
import { signOut } from '../../hooks/useSafeAuth'; // Function to sign user out
import { useSafeAuth as useAuth } from '../../hooks/useSafeAuth'; // Hook to get current user info
// Import custom components
import { InstallButton } from '../InstallButton'; // Button to install app as PWA
import FloatingActionButton from '../mobile/FloatingActionButton'; // Floating + button for quick actions

// Define the shape of props (properties) this mobile layout component expects
// TypeScript interface ensures we pass the right data types
interface MobileLayoutProps {
  // children: The page content that will be displayed inside this mobile layout
  children: React.ReactNode;
  // Optional callback functions for the floating action button
  // The ? makes these optional - they may or may not be provided
  onAddTask?: () => void;        // Function to call when user wants to add a task
  onAddDeliverable?: () => void; // Function to call when user wants to add a deliverable
  onAddMatter?: () => void;      // Function to call when user wants to add a matter
  onAddProject?: () => void;     // Function to call when user wants to add a project
}

// MobileLayout is the main layout component specifically designed for mobile devices
// It provides a slide-out sidebar navigation and floating action button
function MobileLayout({ children, onAddTask, onAddDeliverable, onAddMatter, onAddProject }: MobileLayoutProps) {
  // Get information about the current page URL to highlight active navigation
  const location = useLocation();
  // Get navigation function to programmatically change pages
  const navigate = useNavigate();
  // Get current user information from authentication system
  const { user } = useAuth();
  // State to track whether the side navigation menu is open or closed
  // useState(false) means it starts closed
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Function to handle user signing out
  // async/await is used because signOut might take time (network request)
  const handleSignOut = async () => {
    await signOut(); // Sign the user out of the authentication system
    navigate('/');   // Navigate to the home page after signing out
  };

  // Define the main navigation menu items for mobile
  // Each item has a display name, URL path, and icon
  // Note: This is a subset of desktop navigation - mobile shows fewer items for simplicity
  const mainNavigation = [
    { name: 'Overview', href: '/overview', icon: BarChart2 },      // Dashboard with statistics
    { name: 'Projects', href: '/projects', icon: FolderOpen },     // Project management
    { name: 'Deliverables', href: '/dashboard', icon: CheckSquare }, // Goals and tasks
    { name: 'Regular Tasks', href: '/daily', icon: Calendar },     // Daily recurring tasks
    { name: 'Matters', href: '/matters', icon: Folder },           // Legal matters/cases
    { name: 'Inbox', href: '/inbox', icon: Inbox },               // Quick task capture
  ];

  // Helper function to close the sidebar menu
  // This is called when user clicks outside the sidebar or on a navigation item
  const closeSidebar = () => setIsSidebarOpen(false);

  // Render the mobile layout structure
  return (
    // Main container: minimum full screen height, gray background
    <div className="min-h-screen bg-gray-50">
      {/* Top Header Bar - Fixed at top of screen */}
      {/* fixed: stays at top when scrolling, z-40: appears above other content */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
        {/* Header content: logo on left, menu button on right */}
        <div className="flex items-center justify-between px-4 h-14">
          {/* App logo/name that links to overview page */}
          <Link to="/overview" className="text-xl font-bold text-gray-900 hover:text-gray-700 transition-colors">
            Goalie4
          </Link>
          {/* Hamburger menu button to open sidebar */}
          <button
            onClick={() => setIsSidebarOpen(true)} // When clicked, open the sidebar
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {/* Three horizontal lines icon (hamburger menu) */}
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </header>

      {/* Sidebar Overlay - Dark background that appears when sidebar is open */}
      {/* Only renders when isSidebarOpen is true */}
      {isSidebarOpen && (
        <div 
          // Covers entire screen with semi-transparent black background
          className="fixed inset-0 z-50 bg-black bg-opacity-50 transition-opacity"
          onClick={closeSidebar} // Clicking on overlay closes the sidebar
        />
      )}

      {/* Sidebar Menu - Slides in from the right */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-white z-50 transform transition-transform duration-300 ease-in-out ${
        isSidebarOpen ? 'translate-x-0' : 'translate-x-full' // Slide animation: 0 = visible, full = hidden off-screen
      }`}>
        {/* Sidebar content container - full height flex column */}
        <div className="flex flex-col h-full">
          {/* Sidebar Header with title and close button */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
            {/* X button to close sidebar */}
            <button
              onClick={closeSidebar} // Close sidebar when clicked
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* User Information Section */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {/* User avatar - circular background with user icon */}
              <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              {/* User details */}
              <div className="flex-1 min-w-0">
                {/* Display user's email address, truncated if too long */}
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.email}
                </p>
                {/* User status/plan indicator */}
                <p className="text-xs text-gray-500">Premium Member</p>
              </div>
            </div>
          </div>

          {/* Sidebar Navigation Menu */}
          {/* flex-1: takes remaining space, overflow-y-auto: scrollable if menu is too tall */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {/* Main Navigation Items */}
            {/* Loop through each navigation item and create a link */}
            {mainNavigation.map((item) => {
              // Check if this navigation item matches the current page
              const isActive = location.pathname === item.href;
              // Get the icon component for this navigation item
              const Icon = item.icon;
              return (
                // Create a navigation link
                <Link
                  key={item.name} // React key for list items
                  to={item.href}   // URL to navigate to
                  onClick={closeSidebar} // Close sidebar when user clicks a navigation item
                  // Dynamic styling based on whether this is the active page
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-indigo-50 text-indigo-700' // Active: light blue background, dark blue text
                      : 'text-gray-700 hover:bg-gray-100' // Inactive: gray text, light hover background
                  }`}
                >
                  {/* Display the icon for this navigation item */}
                  <Icon className="w-5 h-5 mr-3" />
                  {/* Display the name of the navigation item */}
                  {item.name}
                </Link>
              );
            })}
            
            {/* Divider line to separate main navigation from secondary items */}
            <div className="my-4 border-t border-gray-200" />
            
            {/* Templates navigation link */}
            <Link
              to="/templates"
              onClick={closeSidebar} // Close sidebar when clicked
              className="flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <FileText className="w-5 h-5 mr-3" />
              Templates
            </Link>
            {/* Settings navigation link */}
            <Link
              to="/settings"
              onClick={closeSidebar} // Close sidebar when clicked
              className="flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Settings className="w-5 h-5 mr-3" />
              Settings
            </Link>
            {/* Install App button */}
            <div className="pt-2">
              <InstallButton />
            </div>
          </nav>

          {/* Sidebar Footer with Sign Out button */}
          <div className="p-4 border-t border-gray-200">
            {/* Sign out button - red colored to indicate destructive action */}
            <button
              onClick={handleSignOut} // Call sign out function when clicked
              className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {/* pt-14: top padding to account for fixed header, px-4: horizontal padding */}
      <main className="pt-14 px-4 pb-4">
        {/* Content container with max width and centered */}
        <div className="max-w-2xl mx-auto">
          {/* Render the page content that was passed as children */}
          {children}
        </div>
      </main>

      {/* Note: Bottom Navigation was removed for cleaner mobile experience */}

      {/* Floating Action Button - Fixed position button for quick actions */}
      {/* Passes through the callback functions for creating different types of items */}
      <FloatingActionButton
        onAddTask={onAddTask}           // Function to add new tasks
        onAddDeliverable={onAddDeliverable} // Function to add new deliverables
        onAddMatter={onAddMatter}       // Function to add new matters
        onAddProject={onAddProject}     // Function to add new projects
      />

    </div>
  );
}

// Export MobileLayout as the default export so other files can import it
export default MobileLayout;
